import React, { useState, useEffect, useRef } from 'react';
import {
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TaskIcon from '@mui/icons-material/Task';
import DescriptionIcon from '@mui/icons-material/Description';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
// LAP-646 — icon for the Social Dashboard quick action.
import InsightsIcon from '@mui/icons-material/Insights';
import { useNavigate } from 'react-router-dom';
// LAP-733 (GE022): translate the floating quick-action menu.
import { useTranslation } from 'react-i18next';

const QuickActions = ({ currentDomain = null }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isMountedRef = useRef(true);

  // Cleanup: close menu on unmount to prevent portal errors
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Close menu before unmounting to prevent portal errors
      if (anchorEl) {
        setAnchorEl(null);
      }
    };
  }, [anchorEl]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (route) => {
    navigate(route);
    handleClose();
  };

  // Context-aware actions based on current domain filter.
  // Labels are pulled from the `common:quickActions.*` namespace so they
  // follow the user's language preference.
  const getActions = () => {
    const addNoteAction = {
      icon: <DescriptionIcon />,
      label: t('quickActions.addNote', { defaultValue: 'Ajouter une note' }),
      route: '/todos/note?action=new-note',
    };
    const baseActions = [
      { icon: <TaskIcon />, label: t('quickActions.addTask'), route: '/todos' },
      addNoteAction,
      { icon: <DescriptionIcon />, label: t('quickActions.addDocument'), route: '/documents' },
      { icon: <LightbulbIcon />, label: t('quickActions.addIdea'), route: '/mesobjectifs' },
      { icon: <PersonAddIcon />, label: t('quickActions.addContact'), route: '/MesContacts' },
    ];

    if (currentDomain === 'Finance') {
      return [
        {
          icon: <DescriptionIcon />,
          label: t('quickActions.addAccount', { defaultValue: 'Ajouter un compte' }),
          route: '/mescomptes?action=new-account',
        },
        { icon: <DescriptionIcon />, label: t('quickActions.addOperation'), route: '/gestionoperations' },
        ...baseActions,
      ];
    }

    if (currentDomain === 'Time') {
      return [
        addNoteAction,
        { icon: <TaskIcon />, label: t('quickActions.addTask'), route: '/todos' },
        { icon: <LightbulbIcon />, label: t('quickActions.addObjective'), route: '/mesobjectifs' },
        ...baseActions.filter(a => !['/todos', '/todos/note?action=new-note'].includes(a.route)),
      ];
    }

    if (currentDomain === 'Home') {
      return [
        { icon: <DescriptionIcon />, label: t('quickActions.addProperty'), route: '/domicile/monimmobilier' },
        { icon: <DescriptionIcon />, label: t('quickActions.addVehicle'), route: '/domicile/mesvehicules' },
        ...baseActions,
      ];
    }

    // LAP-646 — Social quick menu : expose the Social Dashboard as the primary
    // entry, followed by the existing "Ajouter un contact" action and the
    // shared base actions. The dashboard is read-only so we surface it here
    // as a navigation shortcut (the FAB stays a "+" because the rest of the
    // actions are creators).
    if (currentDomain === 'Social') {
      return [
        {
          icon: <InsightsIcon />,
          label: t('quickActions.openSocialDashboard', {
            defaultValue: 'Ouvrir le tableau de bord social',
          }),
          route: '/social-dashboard',
        },
        { icon: <PersonAddIcon />, label: t('quickActions.addContact'), route: '/MesContacts' },
        ...baseActions.filter((a) => a.route !== '/MesContacts'),
      ];
    }

    return baseActions;
  };

  const actions = getActions();

  return (
    <>
      <Fab
        color="primary"
        aria-label="quick actions"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            transform: 'scale(1.1)',
          },
          zIndex: 1000,
        }}
      >
        <AddIcon />
      </Fab>

      <Menu
        anchorEl={anchorEl}
        open={open && !!anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            bgcolor: '#1a1d2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            minWidth: 200,
          },
        }}
      >
        {actions.map((action, index) => (
          <MenuItem
            key={index}
            onClick={() => handleAction(action.route)}
            sx={{
              color: '#E0E0E0',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#E0E0E0' }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default QuickActions;
